import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import instance from '../axios/axios';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import StatBoxWrapper from './StatboxWrapper';
import CircularProgress from '@mui/material/CircularProgress';
import LineChartComponent from "./LineChartComponent";
import MyLineChart from "./ApexLineChartComponent";
import '../css/Dashboard.css'

const oneWeekAgo = new Date();
oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
};

const loaderStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
};


const Dashboard = () => {
    const [dashboards, setDashboards] = useState([]);
    const [startDate, setStartDate] = useState(formatDate(oneWeekAgo));
    const [endDate, setEndDate] = useState(formatDate(new Date()));
    const [lineChartData, setLineChartData] = useState([]);
    const [lineChartLoading, setLineChartLoading] = useState(false);
    const [selectDate, setSelectDate] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [pieChartLoading, setPieChartLoading] = useState(false);
    const [statBoxData, setStatBoxData] = useState([]);
    const [statBoxLoading, setStatBoxLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [noDashboards, setNoDashboards] = useState(false);
    const sectionRef = useRef(null);
    const [dateSwitcher, setDateSwitcher] = useState(false);

    const location = useLocation();

    useEffect(() => {
        switch (selectDate) {
            case '1':
                setStartDate(formatDate(new Date()));
                setEndDate(formatDate(new Date()));
                break;
            case '2':
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setStartDate(formatDate(yesterday));
                setEndDate(formatDate(yesterday));
                break;
            case '3':
                const thisWeek = new Date();
                const thisWeekMonday = thisWeek.getDate() - thisWeek.getDay() + 1;
                const thisWeekSunday = thisWeekMonday + 6;
                setStartDate(formatDate(new Date(thisWeek.setDate(thisWeekMonday))));
                setEndDate(formatDate(new Date(thisWeek.setDate(thisWeekSunday))));
                break;
            case '4':
                const lastWeek = new Date();
                const lastWeekMonday = lastWeek.getDate() - lastWeek.getDay() - 6;
                const lastWeekSunday = lastWeekMonday + 6;
                setStartDate(formatDate(new Date(lastWeek.setDate(lastWeekMonday))));
                setEndDate(formatDate(new Date(lastWeek.setDate(lastWeekSunday))));
                break;
            case '5':
                const thisMonth = new Date();
                const firstDay = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
                const lastDay = new Date(thisMonth.getFullYear(), thisMonth.getMonth() + 1, 0);
                setStartDate(formatDate(firstDay));
                setEndDate(formatDate(lastDay));
                break;
            case '6':
                const lastMonth = new Date();
                const lastMonthFirstDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth() - 1, 1);
                const lastMonthLastDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 0);
                setStartDate(formatDate(lastMonthFirstDay));
                setEndDate(formatDate(lastMonthLastDay));
                break;
            default:
                break;
        }
        setDateSwitcher(true);
    }, [selectDate]);

    useEffect(() => {
        const fetchDashboards = async () => {
            setLoading(true);
            setNoDashboards(false);
            try {
                const result = await instance.get('users/dashboards/access_list_from_jwt', { withCredentials: true });
                if (result.status === 200 && result.data.data.access.length > 0) {
                    setDashboards(result.data.data.access);
                    setNoDashboards(false);
                } else {
                    setNoDashboards(true);
                }
            } catch (error) {
                console.log('Error fetching data:', error);
                setNoDashboards(true);
            }
            setLoading(false);
        };

        fetchDashboards();
        setDateSwitcher(false);
    }, [location, dateSwitcher]);

    useEffect(() => {
        const adjustFlexBasis = () => {
            const nodes = sectionRef.current.querySelectorAll('.chart');
            const count = nodes.length;
            const basis = count === 1 ? '100%' : `calc(${100 / Math.min(3, count)}% - 20px)`;
            nodes.forEach(node => node.style.flex = `1 1 ${basis}`);
        };

        if (dashboards.length) {
            adjustFlexBasis();
        }
    }, [dashboards]);

    useEffect(() => {
        let isMounted = true;
        const processedDashboards = new Set();
        const fetchDashboardsData = async () => {
            if (dashboards.length > 0) {
                if (isMounted) {
                    setLineChartData([]);
                    setPieChartData([]);
                    setStatBoxData([]);
                }

                const hasLineCharts = dashboards.some(d => d.chart_type === "line");
                const hasPieCharts = dashboards.some(d => d.chart_type === "pie");
                const hasStatCharts = dashboards.some(d => d.chart_type === "stats");

                if (!isMounted) return;

                setLineChartLoading(hasLineCharts);
                setPieChartLoading(hasPieCharts);
                setStatBoxLoading(hasStatCharts);

                const completedByType = { line: 0, pie: 0, stats: 0 };
                const totalByType = {
                    line: dashboards.filter(d => d.chart_type === 'line').length,
                    pie: dashboards.filter(d => d.chart_type === 'pie').length,
                    stats: dashboards.filter(d => d.chart_type === "stats").length
                };

                dashboards.forEach(async (dashboard) => {
                    const dashboardKey = `${dashboard.id}-${dashboard.chart_type}`;
                    if (processedDashboards.has(dashboardKey)) return;
                    processedDashboards.add(dashboardKey);

                    try {
                        const result = await instance.post(
                            `reports/dashboards/${dashboard.id}`,
                            { start_date: startDate, end_date: endDate },
                            { withCredentials: true }
                        );

                        if (result.status === 200 && isMounted) {
                            const responseData = result.data[0]?.response;

                            const normalizedData = {
                                id: dashboard.id,
                                name: responseData?.name || dashboard.name || `Dashboard ${dashboard.id}`,
                                data: responseData?.data || null,
                                message: responseData?.message || null,
                                hasData: responseData && !responseData.message && responseData.name,
                                ...responseData
                            };

                            if (dashboard.chart_type === 'line') {
                                setLineChartData(prev => {
                                    if (prev.some(item => item.id === dashboard.id)) return prev;
                                    return [...prev, normalizedData]
                                })
                            } else if (dashboard.chart_type === 'pie') {
                                setPieChartData(prev => {
                                    if (prev.some(item => item.id === dashboard.id)) return prev;
                                    return [...prev, normalizedData]
                                })
                            } else if (dashboard.chart_type === 'stats') {
                                setStatBoxData(prev => {
                                    if (prev.some(item => item.id === dashboard.id)) return prev;
                                    return [...prev, normalizedData]
                                });
                            }
                        }
                    } catch (error) {
                        console.log('Error fetching data:', error);
                    } finally {
                        if (isMounted) {
                            completedByType[dashboard.chart_type]++;

                            if (completedByType.line === totalByType.line) {
                                setLineChartLoading(false);
                            }
                            if (completedByType.pie === totalByType.pie) {
                                setPieChartLoading(false);
                            }
                            if (completedByType.stats === totalByType.stats) {
                                setStatBoxLoading(false);
                            }
                        }
                    }
                });
            }
        };

        fetchDashboardsData();

        return () => {
            isMounted = false;
        };
    }, [dashboards]); // Suggestions are to include startDate and endDate but those can change without being submitted, so I'm not sure

    if (loading) {
        return (
            <div style={loaderStyle}>
                <CircularProgress size={80} />
            </div>
        );
    }

    if (noDashboards && !loading) {
        return (
            <div className="no-dashboards">
                <h1 style={{ textAlign: "center" }}>No Dashboards Available</h1>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="date-select">
                <FormControl sx={{ minWidth: "20vw" }}>
                    <InputLabel id="date-select-label">Select Date Range</InputLabel>
                    <Select
                        labelId="date-select-label"
                        id="date-select"
                        value={selectDate}
                        label="Select Date Range"
                        onChange={(e) => setSelectDate(e.target.value)}
                    >   
                        <MenuItem value="1">Today</MenuItem>
                        <MenuItem value="2">Yesterday</MenuItem>
                        <MenuItem value="3">This Week</MenuItem> 
                        <MenuItem value="4">Last Week</MenuItem> 
                        <MenuItem value="5">This Month</MenuItem> 
                        <MenuItem value="6">Last Month</MenuItem> 
                    </Select>
                </FormControl>
            </div>
            <div ref={sectionRef} className="section">
                {statBoxLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress size={40} />
                        <p>Loading statistics...</p>
                    </div>
                )}
                {statBoxData.map((data, index) => (
                    <div key={index} className="statbox">
                        <h2 style={{ textAlign: "center" }}>{data.name}</h2>
                        {data.hasData ? (
                            <StatBoxWrapper data={data.data} />
                        ) : (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                                <p>{data.message || "No Data Available"}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="section">
                {lineChartLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress size={40} />
                        <p>Loading line charts...</p>
                    </div>
                )}
                {lineChartData.map((data, index) => (
                    <div key={index} className="chart">
                        <h2 style={{ textAlign: "center" }}>{data.name}</h2>
                        {data.hasData ? (
                            <MyLineChart data={data} />
                        ) : (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                                <p>{data.message || "No Data Available"}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="section">
                {pieChartLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress size={40} />
                        <p>Loading pie charts...</p>
                    </div>
                )}
                {pieChartData.map((data, index) => (
                    <div key={index} className="chart">
                        <h2 style={{ textAlign: "center" }}>{data.name}</h2>
                        {data.hasData ? (
                            <PieChart
                                series={[data]}
                                width={500}
                                height={300}
                            />
                        ) : (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                                <p>{data.message || "No Data Available"}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Dashboard;
